import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Button, 
  Grid, 
  ButtonGroup, 
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ComparseArrowsIcon from '@mui/icons-material/CompareArrows';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import { keyframes } from '@mui/system';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useNavigate } from 'react-router-dom';
import AddBankAccount from './AddBankAccount';
import AddSubAccount from './AddSubAccount';
import AddCreditCardModal from './AddCreditCardModal';
import { CreditCardUsageModal, CreditCardPaymentModal, CreditCardTransactionsModal, CreditCardForAction } from './CreditCardActionModals';
import BankDepositModal from './BankDepositModal';
import BankWithdrawModal from './BankWithdrawModal';
import CashTransferModal from '../cashbox/CashTransferModal';
import AccountTransferModal from './AccountTransferModal';
import BankToBankTransferModal from './BankToBankTransferModal';
import BankToCashTransferModal from './BankToCashTransferModal';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AddLoanModal from './loans/AddLoanModal';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import SavingsIcon from '@mui/icons-material/Savings';
import { useTranslation } from 'react-i18next';

// Animasyon tanımı
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

// Banka hesabı arayüzü
interface BankAccount {
  id: string;
  bankName: string;
  branchName: string;
  branchId?: string | null; // GTS şube sistemi
  authorizedPerson?: string | null; // Banka yetkilisi
  authorizedPhone?: string | null; // Yetkili telefon
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  subAccounts?: { [key: string]: SubAccount };
  isPosAccount?: boolean;
  isCheckAccount?: boolean;
  // Alt hesap işlemleri için yeni alanlar
  isSubAccountOperation?: boolean; // Alt hesap üzerinde işlem yapılıp yapılmadığı
  subAccountId?: string; // Alt hesap ID'si
  subAccountName?: string; // Alt hesap adı
}

// Alt hesap arayüzü
interface SubAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  iban: string;
  currency: string;
  balance: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  parentId: string;
  isPosAccount?: boolean;
  isCheckAccount?: boolean;
}

// Kredi kartı arayüzü
interface CreditCard {
  id: number;
  companyId: string;
  bankAccountId?: number | null;
  branchId?: string | null;
  accountName: string;
  description?: string;
  cardNumber: string;
  statementDay: number;
  dueDay: number;
  creditLimit: number;
  minPaymentAmount: number;
  usedAmount: number;
  currency: string;
  isActive: boolean;
  isBranchOnly: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface BankProps {
  isManager?: boolean;
  managerBranchId?: string | null;
}

const Bank: React.FC<BankProps> = ({ isManager = false, managerBranchId = null }) => {
  const { t, i18n } = useTranslation('accounting');
  const navigate = useNavigate();
  const { userDetails, branches } = useAuth(); // ✅ branches'i AuthContext'ten al
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  
  // 🔍 OFFLINE DEBUG - Sayfa mount logu
  const hasLoggedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasLoggedRef.current) {
      hasLoggedRef.current = true;
      console.log('🚀 [BANK] Sayfa MOUNT edildi');
      console.log('🔍 [BANK] AuthContext userDetails:', userDetails ? `✅ companyId: ${userDetails.companyId}` : '❌ YOK');
    }
  }, [userDetails]);

  // State tanımlamaları
  const [isAddBankModalOpen, setIsAddBankModalOpen] = useState(false);
  const [accountToEdit, setAccountToEdit] = useState<BankAccount | null>(null); // Düzenlenecek hesap
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  
  // Banka hesap detayları modalı için state
  const [isAccountDetailsModalOpen, setIsAccountDetailsModalOpen] = useState(false);
  const [selectedAccountForDetails, setSelectedAccountForDetails] = useState<BankAccount | null>(null);
  
  // Alt hesap ekleme modalı için state
  const [isAddSubAccountModalOpen, setIsAddSubAccountModalOpen] = useState(false);
  const [subAccountToEdit, setSubAccountToEdit] = useState<SubAccount | null>(null); // Düzenlenecek alt hesap
  
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Genişletilmiş banka hesaplarını takip için state
  const [expandedAccounts, setExpandedAccounts] = useState<{ [key: string]: boolean }>({});
  
  // Kredi kartları için state'ler
  const [creditCards, setCreditCards] = useState<{ [bankId: string]: CreditCard[] }>({});
  const [isAddCreditCardModalOpen, setIsAddCreditCardModalOpen] = useState(false);
  const [selectedBankForCreditCard, setSelectedBankForCreditCard] = useState<BankAccount | null>(null);
  const [creditCardToEdit, setCreditCardToEdit] = useState<CreditCard | null>(null);

  // Banka kredisi ekleme modalı
  const [addLoanModalOpen, setAddLoanModalOpen] = useState(false);
  const [loanBankAccount, setLoanBankAccount] = useState<BankAccount | null>(null);
  const [loanSnackbar, setLoanSnackbar] = useState<string | null>(null);

  // Kredi kartı işlem modalları
  const [creditCardActionTarget, setCreditCardActionTarget] = useState<CreditCardForAction | null>(null);
  const [isCreditCardUsageModalOpen, setIsCreditCardUsageModalOpen] = useState(false);
  const [isCreditCardPaymentModalOpen, setIsCreditCardPaymentModalOpen] = useState(false);
  const [isCreditCardTxModalOpen, setIsCreditCardTxModalOpen] = useState(false);
  
  // Silme işlemi için state'ler
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [canDelete, setCanDelete] = useState(true); // Hesap silinebilir mi?
  const [deleteCheckLoading, setDeleteCheckLoading] = useState(false); // Kontrol yapılıyor mu?
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Para yatırma modalı için state'ler
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [selectedAccountForDeposit, setSelectedAccountForDeposit] = useState<BankAccount | null>(null);

  // Para çekme modalı için state'ler
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [selectedAccountForWithdraw, setSelectedAccountForWithdraw] = useState<BankAccount | null>(null);

  // CashTransferModal için state
  const [isCashTransferModalOpen, setIsCashTransferModalOpen] = useState(false);

  // Hesaba virman modalı için state
  const [isAccountTransferModalOpen, setIsAccountTransferModalOpen] = useState(false);

  // Bankadan bankaya transfer modalı için state
  const [isBankToBankTransferModalOpen, setIsBankToBankTransferModalOpen] = useState(false);
  const [bankTransferSourceAccount, setBankTransferSourceAccount] = useState<BankAccount | null>(null);

  // Bankadan kasaya transfer modalı için state
  const [isBankToCashTransferModalOpen, setIsBankToCashTransferModalOpen] = useState(false);
  const [bankToCashSourceAccount, setBankToCashSourceAccount] = useState<BankAccount | null>(null);

  // Excel indirme için state
  const [loadingExport, setLoadingExport] = useState(false);

  // Banka dashboard özet verileri (backend'den)
  const [bankSummary, setBankSummary] = useState<{
    totalBalance: number; todayIncomeCount: number; todayIncomeTotal: number;
    todayExpenseCount: number; todayExpenseTotal: number;
    pendingReceivableCount: number; pendingReceivableTotal: number;
    pendingPaymentCount: number; pendingPaymentTotal: number;
  } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadBankSummary = async (compId: string) => {
    try {
      setSummaryLoading(true);
      const response = await accountingApi.get(ACCOUNTING_API.BANK_SUMMARY);
      const result = response.data;
      const data = result.data || result.Data || result;
      setBankSummary({
        totalBalance: data.totalBalance ?? data.TotalBalance ?? 0,
        todayIncomeCount: data.todayIncomeCount ?? data.TodayIncomeCount ?? 0,
        todayIncomeTotal: data.todayIncomeTotal ?? data.TodayIncomeTotal ?? 0,
        todayExpenseCount: data.todayExpenseCount ?? data.TodayExpenseCount ?? 0,
        todayExpenseTotal: data.todayExpenseTotal ?? data.TodayExpenseTotal ?? 0,
        pendingReceivableCount: data.pendingReceivableCount ?? data.PendingReceivableCount ?? 0,
        pendingReceivableTotal: data.pendingReceivableTotal ?? data.PendingReceivableTotal ?? 0,
        pendingPaymentCount: data.pendingPaymentCount ?? data.PendingPaymentCount ?? 0,
        pendingPaymentTotal: data.pendingPaymentTotal ?? data.PendingPaymentTotal ?? 0,
      });
    } catch (err) {
      console.error('❌ [BANK] Summary yükleme hatası:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  // İstatistik hesaplamaları (mevcut verilerden)
  const bankStats = useMemo(() => {
    const totalBalance = filteredAccounts.reduce((sum, acc) => sum + acc.balance, 0);
    const accountCount = filteredAccounts.length;
    const subAccountCount = filteredAccounts.reduce((sum, acc) => {
      return sum + (acc.subAccounts ? Object.keys(acc.subAccounts).length : 0);
    }, 0);
    const posAccountCount = filteredAccounts.filter(acc => acc.isPosAccount).length;
    const posBalance = filteredAccounts.filter(acc => acc.isPosAccount).reduce((sum, acc) => sum + acc.balance, 0);
    return { totalBalance, accountCount, subAccountCount, posAccountCount, posBalance };
  }, [filteredAccounts]);

  // 3 nokta menüsü için state (üst menü)
  const [anchorElMore, setAnchorElMore] = useState<null | HTMLElement>(null);
  
  const handleOpenMoreMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMore(event.currentTarget);
  };
  
  const handleCloseMoreMenu = () => {
    setAnchorElMore(null);
  };

  // Satır bazlı 3 nokta menüsü için state
  const [anchorElRow, setAnchorElRow] = useState<{ [key: string]: HTMLElement | null }>({});
  
  const handleOpenRowMenu = (account: BankAccount | SubAccount, event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    // Alt hesap için unique key oluştur
    const menuKey = isSubAccount(account) ? `sub-${account.id}` : account.id;
    setAnchorElRow(prev => ({ ...prev, [menuKey]: event.currentTarget }));
  };
  
  const handleCloseRowMenu = (account: BankAccount | SubAccount) => {
    // Alt hesap için unique key oluştur
    const menuKey = isSubAccount(account) ? `sub-${account.id}` : account.id;
    setAnchorElRow(prev => ({ ...prev, [menuKey]: null }));
  };
  
  // Menü key'ini al
  const getMenuKey = (account: BankAccount | SubAccount): string => {
    return isSubAccount(account) ? `sub-${account.id}` : account.id;
  };

  // Modal açma kapama fonksiyonları
  const handleOpenAddBankModal = () => setIsAddBankModalOpen(true);
  const handleCloseAddBankModal = () => {
    setIsAddBankModalOpen(false);
    setAccountToEdit(null); // Düzenleme modunu kapat
    // Hesapları yeniden yükle
    if (companyId) {
      loadBankAccounts(companyId);
    }
  };

  // Hesap düzenleme modalını aç
  const handleEditAccount = (account: BankAccount) => {
    console.log('✏️ Hesap düzenleme modalı açılıyor:', account);
    setAccountToEdit(account);
    setIsAddBankModalOpen(true);
  };
  
  const handleOpenTransferModal = () => setIsAccountTransferModalOpen(true);
  const handleCloseTransferModal = () => setIsAccountTransferModalOpen(false);
  
  const handleOpenTransactionReportModal = () => {
    navigate('/bank/transactions');
  };
  
  // Hesap detayları modalı açma/kapama fonksiyonları
  const handleOpenAccountDetailsModal = (account: BankAccount | SubAccount) => {
    // Alt hesap ise, alt hesap bilgilerini koru
    if (isSubAccount(account)) {
      const parentAccount = filteredAccounts.find(a => a.id === account.parentId);
      if (parentAccount) {
        // Alt hesabı ana hesaba bağla ama kendi bilgilerini koru
        const accountWithSubAccountInfo: BankAccount = {
          id: parentAccount.id, // Ana hesabın ID'si
          bankName: parentAccount.bankName,
          branchName: parentAccount.branchName,
          accountNumber: account.accountNumber,
          iban: account.iban,
          currency: account.currency,
          balance: account.balance,
          createdAt: account.createdAt,
          updatedAt: account.updatedAt,
          isActive: account.isActive,
          isPosAccount: account.isPosAccount,
          isCheckAccount: account.isCheckAccount,
          subAccounts: parentAccount.subAccounts,
          // Alt hesap bilgilerini özel alanlar olarak ekle
          isSubAccountOperation: true,
          subAccountId: account.id, // Alt hesabın ID'si sakla
          subAccountName: account.accountName
        };
        
        setSelectedAccountForDetails(accountWithSubAccountInfo);
      }
    } else {
      // Ana hesapsa normal işlem yap
      setSelectedAccountForDetails({
        ...account,
        isSubAccountOperation: false
      });
    }
    setIsAccountDetailsModalOpen(true);
  };
  
  const handleCloseAccountDetailsModal = () => {
    setIsAccountDetailsModalOpen(false);
    setSelectedAccountForDetails(null);
  };
  
  // Alt hesap ekleme modalı açma/kapama fonksiyonları
  const handleOpenAddSubAccountModal = () => {
    if (selectedAccountForDetails) {
      setIsAddSubAccountModalOpen(true);
    }
  };
  
  const handleCloseAddSubAccountModal = () => {
    setIsAddSubAccountModalOpen(false);
    setSubAccountToEdit(null); // Düzenleme modunu kapat
    // Hesapları yeniden yükle
    if (companyId) {
      loadBankAccounts(companyId);
    }
  };

  // Alt hesap düzenleme modalını aç
  const handleEditSubAccount = (subAccount: SubAccount, parentAccount: BankAccount) => {
    console.log('✏️ Alt hesap düzenleme modalı açılıyor:', subAccount);
    setSelectedAccountForDetails(parentAccount); // Parent hesabı seç
    setSubAccountToEdit(subAccount);
    setIsAddSubAccountModalOpen(true);
  };

  // Kredi kartı modal fonksiyonları
  const handleOpenAddCreditCardModal = (bankAccount: BankAccount) => {
    console.log('💳 Kredi kartı ekleme modalı açılıyor:', bankAccount.bankName);
    setSelectedBankForCreditCard(bankAccount);
    setCreditCardToEdit(null);
    setIsAddCreditCardModalOpen(true);
  };

  const handleEditCreditCard = (card: CreditCard, bankAccount: BankAccount) => {
    console.log('✏️ Kredi kartı düzenleme modalı açılıyor:', card.accountName);
    setSelectedBankForCreditCard(bankAccount);
    setCreditCardToEdit(card as any); // Type assertion for modal
    setIsAddCreditCardModalOpen(true);
  };

  const handleCloseAddCreditCardModal = () => {
    setIsAddCreditCardModalOpen(false);
    setSelectedBankForCreditCard(null);
    setCreditCardToEdit(null);
    // Kredi kartlarını yeniden yükle
    if (companyId) {
      loadCreditCards(companyId);
    }
  };

  // Kredi kartı işlem modalı yardımcıları
  const toCardForAction = (card: any): CreditCardForAction => ({
    id: card.id,
    accountName: card.accountName || '',
    cardNumber: card.cardNumber || '',
    creditLimit: Number(card.creditLimit) || 0,
    usedAmount: Number(card.usedAmount) || 0,
    currency: card.currency || 'TRY',
  });

  const handleOpenCreditCardUsage = (card: any) => {
    setCreditCardActionTarget(toCardForAction(card));
    setIsCreditCardUsageModalOpen(true);
  };

  const handleOpenCreditCardPayment = (card: any) => {
    setCreditCardActionTarget(toCardForAction(card));
    setIsCreditCardPaymentModalOpen(true);
  };

  const handleOpenCreditCardTransactions = (card: any) => {
    setCreditCardActionTarget(toCardForAction(card));
    setIsCreditCardTxModalOpen(true);
  };

  const handleCreditCardActionSuccess = () => {
    if (companyId) {
      loadCreditCards(companyId);
    }
  };

  const handleDeleteCreditCard = async (card: any) => {
    const confirmMsg = i18n.language.startsWith('tr')
      ? `"${card.accountName}" kredi kartını silmek istediğinize emin misiniz?`
      : `Are you sure you want to delete the credit card "${card.accountName}"?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await accountingApi.post(ACCOUNTING_API.CREDIT_CARD_DELETE(card.id), {}, {
        params: { deletedBy: userDetails?.email || 'Sistem' },
      });
      if (companyId) {
        loadCreditCards(companyId);
      }
    } catch (err: any) {
      console.error('Kredi kartı silinemedi:', err);
      setError((i18n.language.startsWith('tr') ? 'Kredi kartı silinemedi: ' : 'Could not delete credit card: ') + (err?.response?.data?.message || err.message));
    }
  };

  // Kredi kartlarını yükle
  const loadCreditCards = async (companyId: string) => {
    try {
      const apiUrl = ACCOUNTING_API.CREDIT_CARD_BY_COMPANY;
      console.log('💳 Kredi kartları yükleniyor... URL:', apiUrl);
      
      const response = await accountingApi.get(apiUrl);
      
      console.log('💳 Kredi kartları API yanıt:', response.status, response.statusText);
      
      const result = response.data;
      const cards: CreditCard[] = result.data || result;
      console.log('✅ Kredi kartları yüklendi:', cards.length, 'adet', cards);
      
      // Kartları bankAccountId'ye göre grupla
      const cardsByBank: { [bankId: string]: CreditCard[] } = {};
      cards.forEach(card => {
        const bankId = card.bankAccountId?.toString() || 'unassigned';
        console.log('💳 Kart gruplanıyor:', card.accountName, 'bankId:', bankId);
        if (!cardsByBank[bankId]) {
          cardsByBank[bankId] = [];
        }
        cardsByBank[bankId].push(card);
      });
      
      console.log('💳 Gruplandırılmış kartlar:', cardsByBank);
      setCreditCards(cardsByBank);
    } catch (error: any) {
      console.error('❌ Kredi kartları yüklenirken hata:', error);
      if (error.response) {
        console.error('❌ Kredi kartları API hatası:', error.response.status, error.response.data);
      }
    }
  };

  // Bir banka hesabının kredi kartları var mı kontrol et
  const hasCreditCards = (bankAccountId: string): boolean => {
    return creditCards[bankAccountId] !== undefined && 
           creditCards[bankAccountId].length > 0;
  };
  
  // Bir hesabı genişlet/daralt
  const toggleAccountExpand = (accountId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };
  
  // Bir hesabın alt hesapları var mı kontrol et
  const hasSubAccounts = (account: BankAccount): boolean => {
    return account.subAccounts !== undefined && 
           Object.keys(account.subAccounts).length > 0;
  };

  // Bir hesabın genişletilebilir alt öğeleri var mı (alt hesap veya kredi kartı)
  const hasExpandableItems = (account: BankAccount): boolean => {
    const subAccountCount = account.subAccounts ? Object.keys(account.subAccounts).length : 0;
    const creditCardCount = creditCards[account.id]?.length || 0;
    return subAccountCount > 0 || creditCardCount > 0;
  };

  // SQL API'den veri çekme
  const dataLoadedRef = React.useRef(false);
  
  useEffect(() => {
    // Zaten yüklendiyse tekrar yükleme
    if (dataLoadedRef.current) {
      return;
    }
    
    // userDetails henüz yüklenmediyse bekle
    if (userDetails?.companyId || ownAuthSession.getCurrentUid()) {
      dataLoadedRef.current = true;
      loadCompanyAndData();
    }
  }, [userDetails?.companyId]);
  
  // ✅ Manager modu: aktif şube değişince listeyi yeniden yükle
  useEffect(() => {
    if (isManager && companyId) {
      loadBankAccounts(companyId);
    }
  }, [managerBranchId]);

  // Sayfa her odaklandığında (focus) verileri yenile
  useEffect(() => {
    const handleFocus = () => {
      console.log('🔄 [BANK] Sayfa odaklandı, veriler yenileniyor...');
      if (companyId) {
        loadBankAccounts(companyId);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [companyId]);

  const loadCompanyAndData = async () => {
    console.log('🔄 [BANK] loadCompanyAndData başladı...');
    
    try {
      setLoading(true);
      
      // 🔍 OFFLINE DEBUG - AuthContext'ten companyId alınabilir mi?
      if (userDetails?.companyId) {
        console.log('✅ [BANK] AuthContext\'ten companyId alındı:', userDetails.companyId);
        setCompanyId(userDetails.companyId);
        
        // Banka hesaplarını SQL'den yükle
        console.log('📥 [BANK] Banka hesapları yükleniyor (SQL API)...');
        await loadBankAccounts(userDetails.companyId);
        return;
      }
      
      // Fallback: Firebase'den kullanıcı bilgilerini al
      console.log('⚠️ [BANK] AuthContext\'te companyId yok, Firebase\'den alınıyor...');
      
      // Kullanıcı oturum açmış mı kontrol et
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        console.error('❌ [BANK] Firebase auth.currentUser YOK');
        setLoading(false);
        setError(t('bank.errors.sessionNotFound'));
        return;
      }
      
      console.log('📡 [BANK] Cloud Functions users/ okunuyor...');
      // Kullanıcının şirket ID'sini al (Cloud Functions)
      const userData = await firebaseDataService.getUserData(uid);
      
      if (!userData) {
        console.error('❌ [BANK] Cloud Functions users/ verisi YOK');
        setLoading(false);
        setError(t('bank.errors.userNotFound'));
        return;
      }
      
      const fetchedCompanyId = userData.companyId;
      console.log('✅ [BANK] Cloud Functions\'dan companyId alındı:', fetchedCompanyId);
      setCompanyId(fetchedCompanyId || null);
      
      if (!fetchedCompanyId) {
        console.error('❌ [BANK] companyId boş');
        setLoading(false);
        setError(t('bank.errors.companyNotFound'));
        return;
      }
      
      // Banka hesaplarını SQL'den yükle
      console.log('📥 [BANK] Banka hesapları yükleniyor (SQL API)...');
      await loadBankAccounts(fetchedCompanyId);
      loadBankSummary(fetchedCompanyId);
      
    } catch (error) {
      console.error('❌ [BANK] Veri yüklenirken hata:', error);
      setLoading(false);
      setError(t('bank.errors.loadFailed'));
    }
  };

  // Banka hesaplarını SQL API'den yükleme
  const loadBankAccounts = async (companyId: string) => {
    const apiUrl = ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY;
    console.log('🌐 [BANK] SQL API çağrısı:', apiUrl);
    
    try {
      // SQL API'den banka hesaplarını yükle
      const response = await accountingApi.get(apiUrl);
      
      console.log('📡 [BANK] API yanıt status:', response.status);
      
      const result = response.data;
      const sqlAccounts = result.data || result;
      
      console.log('✅ [BANK] SQL\'den', sqlAccounts?.length || 0, 'hesap yüklendi');
      
      if (!sqlAccounts || sqlAccounts.length === 0) {
        setBankAccounts([]);
        setFilteredAccounts([]);
        setLoading(false);
        return;
      }
      
      // SQL verilerini frontend formatına çevir
      const accountsList: BankAccount[] = sqlAccounts.map((data: any) => {
        // Alt hesapları dönüştür
        const subAccountsObj: { [key: string]: SubAccount } = {};
        if (data.subAccounts && Array.isArray(data.subAccounts)) {
          data.subAccounts.forEach((subAccount: any) => {
            subAccountsObj[subAccount.id.toString()] = {
              id: subAccount.id.toString(),
              accountName: subAccount.accountName || '',
              accountNumber: subAccount.accountNumber || '',
              iban: subAccount.iban || '',
              currency: subAccount.currency || 'TRY',
              balance: parseFloat(subAccount.balance) || 0,
              createdAt: subAccount.createdAt || '',
              updatedAt: subAccount.updatedAt || '',
              isActive: subAccount.isActive !== false,
              parentId: data.id.toString(),
              isPosAccount: subAccount.isPosAccount || false,
              isCheckAccount: subAccount.isCheckAccount || false
            };
          });
        }
        
        return {
          id: data.id.toString(),
          bankName: data.bankName || '',
          branchName: data.branchName || '',
          branchId: data.branchId || null, // ✅ GTS şube sistemi
          authorizedPerson: data.authorizedPerson || null, // ✅ Banka yetkilisi
          authorizedPhone: data.authorizedPhone || null, // ✅ Yetkili telefon
          accountNumber: data.accountNumber || '',
          iban: data.iban || '',
          currency: data.currency || 'TRY',
          balance: parseFloat(data.balance) || 0,
          createdAt: data.createdAt || '',
          updatedAt: data.updatedAt || '',
          isActive: data.isActive !== false,
          subAccounts: Object.keys(subAccountsObj).length > 0 ? subAccountsObj : undefined,
          isPosAccount: data.isPosAccount || false,
          isCheckAccount: data.isCheckAccount || false
        };
      });
      
      // Hesapları bankaya göre sıralama
      accountsList.sort((a, b) => a.bankName.localeCompare(b.bankName));
      
      // ✅ Manager modu: sadece kendi şubesine ait hesaplar
      const visibleAccounts = (isManager && managerBranchId)
        ? accountsList.filter(a => a.branchId === managerBranchId)
        : accountsList;
      
      console.log('✅ [BANK] Aktif hesap sayısı:', visibleAccounts.length);
      console.log('🎉 [BANK] Sayfa yükleme TAMAMLANDI');
      
      setBankAccounts(visibleAccounts);
      setFilteredAccounts(visibleAccounts);
      setLoading(false);
      
      // Kredi kartlarını da yükle
      await loadCreditCards(companyId);
    } catch (error: any) {
      console.error('❌ [BANK] SQL yükleme hatası:', error.message || error);
      console.error('❌ [BANK] Detay:', error);
      setLoading(false);
      setError(t('bank.errors.loadBankAccountsFailed'));
    }
  };

  // Arama fonksiyonu
  useEffect(() => {
    if (bankAccounts.length === 0) {
      setFilteredAccounts([]);
      return;
    }
    
    if (searchTerm.trim() === '') {
      setFilteredAccounts(bankAccounts);
    } else {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filtered = bankAccounts.filter(account => 
        account.bankName.toLowerCase().includes(lowerCaseSearchTerm) ||
        account.branchName.toLowerCase().includes(lowerCaseSearchTerm) ||
        account.accountNumber.includes(searchTerm) ||
        account.iban.toLowerCase().includes(lowerCaseSearchTerm)
      );
      setFilteredAccounts(filtered);
    }
    setPage(0); // Arama yapıldığında ilk sayfaya dön
  }, [searchTerm, bankAccounts]);
  
  // Hiyerarşik hesap listesi oluştur
  const hierarchicalAccounts = useMemo(() => {
    const result: (BankAccount | SubAccount | CreditCard & { parentId: string; isCreditCard: true })[] = [];
    
    filteredAccounts.forEach(account => {
      // Ana hesabı ekle
      result.push(account);
      
      // Genişletilmişse alt hesapları ekle
      if (expandedAccounts[account.id]) {
        // Alt hesapları ekle
        if (account.subAccounts) {
          Object.entries(account.subAccounts).forEach(([subId, subAccount]) => {
            result.push({
              ...subAccount,
              id: subId,
              parentId: account.id
            });
          });
        }
        
        // Kredi kartlarını ekle
        const bankCreditCards = creditCards[account.id] || [];
        bankCreditCards.forEach(card => {
          result.push({
            ...card,
            parentId: account.id,
            isCreditCard: true
          } as CreditCard & { parentId: string; isCreditCard: true });
        });
      }
    });
    
    return result;
  }, [filteredAccounts, expandedAccounts, creditCards]);
  
  // Sayfalandırılmış hesaplar
  const paginatedAccounts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return hierarchicalAccounts.slice(startIndex, startIndex + rowsPerPage);
  }, [hierarchicalAccounts, page, rowsPerPage]);

  // AccountingDataTable kolon tanımları (hiyerarşik yapı için)
  const bankColumns = useMemo<DataTableColumn<any>[]>(() => [
    {
      id: 'rowNumber',
      label: t('bank.table.columns.row'),
      width: '60px',
      render: (row: any, index: number) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {isCreditCardItem(row) ? '-' : page * rowsPerPage + index + 1}
        </Box>
      ),
    },
    {
      id: 'bankName',
      label: t('bank.table.columns.bankName'),
      sortable: true,
      render: (row: any) => {
        if (isCreditCardItem(row)) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ ml: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCardIcon fontSize="small" color="secondary" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.accountName}</Typography>
                <Chip label={t('bank.labels.creditCard')} size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isSubAccount(row) && hasExpandableItems(row as BankAccount) ? (
              <IconButton size="small" onClick={(e) => toggleAccountExpand(row.id, e)} sx={{ p: 0.5, mr: 1 }}>
                {expandedAccounts[row.id] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
              </IconButton>
            ) : isSubAccount(row) ? (
              <Box sx={{ ml: 4 }} />
            ) : (
              <Box sx={{ width: 28, ml: 0.5, mr: 1 }} />
            )}
            {isSubAccount(row) ? row.accountName : row.bankName}
          </Box>
        );
      },
    },
    {
      id: 'branchName',
      label: t('bank.table.columns.branch'),
      sortable: true,
      render: (row: any) => <>{isCreditCardItem(row) || isSubAccount(row) ? '-' : row.branchName || '-'}</>,
    },
    {
      id: 'companyBranch',
      label: t('bank.table.columns.companyBranch'),
      render: (row: any) => {
        if (isCreditCardItem(row)) return <>{row.branchId ? getBranchName(row.branchId) : '-'}</>;
        if (isSubAccount(row)) return <>-</>;
        return <>{getBranchName((row as BankAccount).branchId)}</>;
      },
    },
    {
      id: 'accountNumber',
      label: t('bank.table.columns.accountNumber'),
      field: 'accountNumber' as any,
      sortable: true,
      render: (row: any) => <>{isCreditCardItem(row) ? `**** ${row.cardNumber.slice(-4)}` : row.accountNumber}</>,
    },
    {
      id: 'accountType',
      label: t('bank.table.columns.accountType'),
      render: (row: any) => {
        if (isCreditCardItem(row)) return <>{t('bank.labels.statementDay', { day: row.statementDay })}<br/>{t('bank.labels.paymentDay', { day: row.dueDay })}</>;
        return <>{getAccountType(row)}</>;
      },
    },
    {
      id: 'iban',
      label: 'IBAN',
      field: 'iban' as any,
      sortable: true,
      render: (row: any) => <>{isCreditCardItem(row) ? '-' : row.iban || '-'}</>,
    },
    {
      id: 'currency',
      label: t('bank.table.columns.currency'),
      field: 'currency' as any,
      sortable: true,
      render: (row: any) => <>{row.currency} ({getCurrencySymbol(row.currency)})</>,
    },
    {
      id: 'balance',
      label: t('bank.table.columns.balance'),
      align: 'right',
      sortable: true,
      render: (row: any) => {
        if (isCreditCardItem(row)) {
          return (
            <Tooltip title={t('bank.labels.cardLimitTooltip', { value: `${getCurrencySymbol(row.currency)}${row.creditLimit.toLocaleString(locale)}` })}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  {t('bank.labels.spent')}: {row.usedAmount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(row.currency)}
                </Typography>
                <Typography variant="caption" color="success.main">
                  {t('bank.labels.availableLimit')}: {getCurrencySymbol(row.currency)}{Math.max(0, row.creditLimit - row.usedAmount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Tooltip>
          );
        }
        const bal = getTotalBalance(row);
        return (
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: bal >= 0 ? 'success.main' : 'error.main' }}>
            {bal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(row.currency)}
          </Typography>
        );
      },
    },
    {
      id: 'actions',
      label: t('bank.table.columns.actions'),
      align: 'right',
      render: (row: any) => {
        if (isCreditCardItem(row)) {
          // ✅ Manager modu: kredi kartı işlemleri kapalı
          if (isManager) {
            return null;
          }
          const card = row;
          const parentAccount = filteredAccounts.find(a => a.id === card.parentId);
          return (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorElRow(prev => ({ ...prev, [`credit-${card.id}`]: e.currentTarget })); }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={anchorElRow[`credit-${card.id}`]}
                open={Boolean(anchorElRow[`credit-${card.id}`])}
                onClose={() => setAnchorElRow(prev => ({ ...prev, [`credit-${card.id}`]: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${card.id}`]: null })); handleOpenCreditCardTransactions(card); }}>
                  <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.transactions')} />
                </MenuItem>
                <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${card.id}`]: null })); if (parentAccount) handleEditCreditCard(card, parentAccount); }}>
                  <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.edit')} />
                </MenuItem>
                <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${card.id}`]: null })); handleOpenCreditCardUsage(card); }}>
                  <ListItemIcon><CreditCardIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.addExpense')} />
                </MenuItem>
                <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${card.id}`]: null })); handleOpenCreditCardPayment(card); }}>
                  <ListItemIcon><AccountBalanceWalletIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.makePayment')} />
                </MenuItem>
                <MenuItem onClick={() => { setAnchorElRow(prev => ({ ...prev, [`credit-${card.id}`]: null })); handleDeleteCreditCard(card); }}>
                  <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.delete')} />
                </MenuItem>
              </Menu>
            </Box>
          );
        }
        // Normal banka/alt hesap satırı
        return (
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {!isManager && (
              <Button size="small" variant="outlined" color="info" startIcon={<InfoIcon fontSize="small" />} onClick={(e) => { e.stopPropagation(); handleOpenAccountDetailsModal(row); }}>
                {t('bank.actions.process')}
              </Button>
            )}
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenRowMenu(row, e); }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorElRow[getMenuKey(row)]}
              open={Boolean(anchorElRow[getMenuKey(row)])}
              onClose={() => handleCloseRowMenu(row)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { handleCloseRowMenu(row); if (isSubAccount(row)) { const parentAcc = filteredAccounts.find(a => a.id === row.parentId); if (parentAcc) handleEditSubAccount(row as SubAccount, parentAcc); } else { handleEditAccount(row as BankAccount); } }}>
                <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary={t('bank.actions.edit')} />
              </MenuItem>
              {!isManager && (
                <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenBankToBankTransferModal(row); }}>
                  <ListItemIcon><SwapHorizIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.bankTransfer')} />
                </MenuItem>
              )}
              {!isManager && (
                <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenBankToCashTransferModal(row); }}>
                  <ListItemIcon><AccountBalanceWalletIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.cashTransfer')} />
                </MenuItem>
              )}
              {!isManager && !isSubAccount(row) && (
                <MenuItem onClick={() => { handleCloseRowMenu(row); setSelectedAccountForDetails(row as BankAccount); setIsAddSubAccountModalOpen(true); }}>
                  <ListItemIcon><AddIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.addSubAccount')} />
                </MenuItem>
              )}
              {!isManager && !isSubAccount(row) && (
                <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenAddCreditCardModal(row as BankAccount); }}>
                  <ListItemIcon><CreditCardIcon fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.addCreditCard')} />
                </MenuItem>
              )}
              {!isManager && !isSubAccount(row) && (
                <MenuItem onClick={() => { handleCloseRowMenu(row); setLoanBankAccount(row as BankAccount); setAddLoanModalOpen(true); }}>
                  <ListItemIcon><RequestQuoteIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary={locale === 'tr-TR' ? 'Kredi Ekle' : 'Add Loan'} />
                </MenuItem>
              )}
              {!isManager && row.isCheckAccount && (
                <MenuItem onClick={() => { handleCloseRowMenu(row); if (isSubAccount(row)) { navigate(`/checkbooks?subAccountId=${row.id}&bankAccountId=${row.parentId}`); } else { navigate(`/checkbooks?bankAccountId=${row.id}`); } }}>
                  <ListItemIcon><MenuBookIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.checkBooks')} />
                </MenuItem>
              )}
              {!isManager && (
                <MenuItem onClick={() => { handleCloseRowMenu(row); handleOpenDeleteDialog(isSubAccount(row) ? { ...row, bankName: filteredAccounts.find(a => a.id === row.parentId)?.bankName || '', branchName: filteredAccounts.find(a => a.id === row.parentId)?.branchName || '' } as BankAccount : row as BankAccount); }}>
                  <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText primary={t('bank.actions.delete')} />
                </MenuItem>
              )}
            </Menu>
          </Box>
        );
      },
    },
  ], [page, rowsPerPage, expandedAccounts, anchorElRow, filteredAccounts, creditCards, locale, t]);
  
  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Hesap türünü döndür
  const getAccountType = (account: BankAccount | SubAccount): string => {
    if (account.isPosAccount && account.isCheckAccount) {
      return t('bank.accountTypes.posAndCheck');
    } else if (account.isPosAccount) {
      return t('bank.accountTypes.pos');
    } else if (account.isCheckAccount) {
      return t('bank.accountTypes.check');
    }
    return t('bank.accountTypes.standard');
  };

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return currency;
    }
  };

  // Şube adını bul (GTS şube sistemi)
  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return '-';
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : '-';
  };

  // Hesap bakiyesini döndür (her hesap sadece kendi bakiyesini gösterir)
  const getTotalBalance = (account: BankAccount | SubAccount): number => {
    return account.balance;
  };

  // Hesap silme işlemleri
  const handleOpenDeleteDialog = async (account: BankAccount) => {
    setSelectedAccount(account);
    setDeleteDialogOpen(true);
    setCanDelete(true);
    setDeleteCheckLoading(true);
    setError(null);
    
    // Hesabın silinip silinemeyeceğini kontrol et
    try {
      const numericId = parseInt(account.id, 10);
      if (isNaN(numericId)) {
        setError(t('bank.errors.invalidAccountId'));
        setCanDelete(false);
        setDeleteCheckLoading(false);
        return;
      }
      
      // Alt hesap olup olmadığını kontrol et
      const isSubAcc = 'parentId' in account;
      
      if (isSubAcc) {
        // ALT HESAP - sadece işlem geçmişi kontrolü
        const response = await accountingApi.get(ACCOUNTING_API.BANK_SUB_ACCOUNT_TRANSACTIONS(numericId));
        
        const result = response.data;
        const transactions = result.data || result;
        
        if (transactions && transactions.length > 0) {
          setCanDelete(false);
          setError(t('bank.errors.subAccountHasHistory'));
        }
      } else {
        // ANA HESAP - hem alt hesap hem işlem geçmişi kontrolü
        
        // 1. Alt hesap kontrolü (frontend'de de yapıyoruz)
        if (account.subAccounts && Object.keys(account.subAccounts).length > 0) {
          setCanDelete(false);
          setError(t('bank.errors.hasSubAccounts'));
          setDeleteCheckLoading(false);
          return;
        }
        
        // 2. İşlem geçmişi kontrolü
        const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_TRANSACTIONS(numericId));
        
        const result = response.data;
        const transactions = result.data || result;
        
        if (transactions && transactions.length > 0) {
          setCanDelete(false);
          setError(t('bank.errors.accountHasHistory'));
        }
      }
    } catch (error) {
      console.error('Hesap kontrol hatası:', error);
      // Hata durumunda yine de silmeye izin ver, backend kontrol edecek
    } finally {
      setDeleteCheckLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedAccount(null);
    setError(null); // Error'u temizle
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount || !companyId) return;
    
    setIsDeleting(true);
    try {
      // Alt hesap olup olmadığını kontrol et
      const isSubAcc = 'parentId' in selectedAccount;
      const accountTypeLabel = isSubAcc ? t('bank.labels.subAccount') : t('bank.labels.bankAccount');
      
      console.log(`🗑️ ${accountTypeLabel} siliniyor (SQL ID:`, selectedAccount.id, ')');
      
      // Hesap ID'yi sayıya çevir (SQL ID)
      const numericId = parseInt(selectedAccount.id, 10);
      if (isNaN(numericId)) {
        throw new Error(`Geçersiz ${accountTypeLabel.toLowerCase()} ID formatı`);
      }
      
      // Uygun API endpoint'ini seç
      const deleteEndpoint = isSubAcc 
        ? ACCOUNTING_API.BANK_SUB_ACCOUNT_DELETE(numericId)
        : ACCOUNTING_API.BANK_ACCOUNT_DELETE(numericId);
      
      // SQL API'ye silme isteği gönder
      await accountingApi.post(deleteEndpoint);

      console.log(`✅ ${accountTypeLabel} silindi (SQL)`);
      
      // Hesapları yeniden yükle
      try {
        await loadBankAccounts(companyId);
      } catch (reloadError) {
        console.error('❌ Hesaplar yeniden yüklenirken hata:', reloadError);
      }
      
      setDeleteDialogOpen(false);
      setError(null);
    } catch (error: any) {
      const isSubAcc = selectedAccount && 'parentId' in selectedAccount;
      const accountTypeLabel = isSubAcc ? t('bank.labels.subAccount') : t('bank.labels.bankAccount');
      console.error(`❌ ${accountTypeLabel} silinirken hata oluştu:`, error);
      
      const errorData = error.response?.data || {};
      let errorMessage = `${accountTypeLabel} silinirken bir hata oluştu.`;
      if (errorData.message) {
        if (errorData.message.includes('Hareket geçmişi bulunan') || errorData.message.includes('işlem geçmişi')) {
          errorMessage = `⚠️ Bu ${accountTypeLabel.toLowerCase()}ta işlem geçmişi bulunmaktadır. İşlem geçmişi olan hesaplar güvenlik nedeniyle silinemez.`;
        } else if (errorData.message.includes('Alt hesabı bulunan') || errorData.message.includes('alt hesap')) {
          errorMessage = t('bank.errors.hasSubAccounts');
        } else {
          errorMessage = errorData.message;
        }
      }
      
      setError(errorMessage);
      setIsDeleting(false);
    } finally {
      if (!error) {
        setIsDeleting(false);
      }
    }
  };

  // Satır koşullu arka plan renkleri (hiyerarşik yapı için)
  const getBankRowSx = (row: any) => {
    if (isCreditCardItem(row)) {
      return {
        bgcolor: 'rgba(156, 39, 176, 0.02)',
        '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.04) !important' },
      };
    }
    if (isSubAccount(row)) {
      return { bgcolor: 'rgba(0, 0, 0, 0.02)' };
    }
    return {};
  };

  // Bir hesap alt hesap mı kontrolü
  const isSubAccount = (account: any): account is SubAccount => {
    return 'parentId' in account && !('isCreditCard' in account);
  };

  // Bir kayıt kredi kartı mı kontrolü
  const isCreditCardItem = (item: any): item is (CreditCard & { parentId: string; isCreditCard: true }) => {
    return 'isCreditCard' in item && item.isCreditCard === true;
  };

  // Para yatırma modalını açma fonksiyonu
  const handleOpenDepositModal = () => {
    if (selectedAccountForDetails) {
      setSelectedAccountForDeposit(selectedAccountForDetails);
      setIsDepositModalOpen(true);
      setIsAccountDetailsModalOpen(false); // Detay modalını kapat
    }
  };

  // Para yatırma modalını kapatma fonksiyonu
  const handleCloseDepositModal = () => {
    setIsDepositModalOpen(false);
    setSelectedAccountForDeposit(null);
    // Hesapları yeniden yükle
    if (companyId) {
      loadBankAccounts(companyId);
    }
  };

  // Para çekme modalını açma fonksiyonu
  const handleOpenWithdrawModal = () => {
    if (selectedAccountForDetails) {
      setSelectedAccountForWithdraw(selectedAccountForDetails);
      setIsWithdrawModalOpen(true);
      setIsAccountDetailsModalOpen(false); // Detay modalını kapat
    }
  };

  // Para çekme modalını kapatma fonksiyonu
  const handleCloseWithdrawModal = () => {
    setIsWithdrawModalOpen(false);
    setSelectedAccountForWithdraw(null);
    // Hesapları yeniden yükle
    if (companyId) {
      loadBankAccounts(companyId);
    }
  };

  // CashTransferModal açma/kapama fonksiyonları
  const handleOpenCashTransferModal = () => {
    if (selectedAccountForDetails) {
      setIsCashTransferModalOpen(true);
      setIsAccountDetailsModalOpen(false); // Detay modalını kapat
    }
  };
  
  const handleCloseCashTransferModal = () => {
    setIsCashTransferModalOpen(false);
  };

  // Bankadan bankaya transfer modalı açma/kapama fonksiyonları
  const handleOpenBankToBankTransferModal = (account: BankAccount | SubAccount) => {
    // Alt hesap mı ana hesap mı kontrol et
    if (isSubAccount(account)) {
      const parentAccount = filteredAccounts.find(a => a.id === account.parentId);
      if (parentAccount) {
        // Alt hesap için kaynak hesap bilgisi
        setBankTransferSourceAccount({
          id: parentAccount.id,
          bankName: parentAccount.bankName,
          branchName: parentAccount.branchName,
          accountNumber: account.accountNumber,
          iban: account.iban,
          currency: account.currency,
          balance: account.balance,
          createdAt: account.createdAt,
          updatedAt: account.updatedAt,
          isActive: account.isActive,
          subAccounts: parentAccount.subAccounts,
          isSubAccountOperation: true,
          subAccountId: account.id,
          subAccountName: account.accountName
        });
      }
    } else {
      // Ana hesap için kaynak hesap bilgisi
      setBankTransferSourceAccount({
        ...account,
        isSubAccountOperation: false
      });
    }
    setIsBankToBankTransferModalOpen(true);
  };
  
  const handleCloseBankToBankTransferModal = () => {
    setIsBankToBankTransferModalOpen(false);
    setBankTransferSourceAccount(null);
    // Hesapları yeniden yükle
    if (companyId) {
      loadBankAccounts(companyId);
    }
  };

  // Bankadan kasaya transfer modalı açma/kapama fonksiyonları
  const handleOpenBankToCashTransferModal = (account: BankAccount | SubAccount) => {
    // Alt hesap mı ana hesap mı kontrol et
    if (isSubAccount(account)) {
      const parentAccount = filteredAccounts.find(a => a.id === account.parentId);
      if (parentAccount) {
        // Alt hesap için kaynak hesap bilgisi
        setBankToCashSourceAccount({
          id: parentAccount.id,
          bankName: parentAccount.bankName,
          branchName: parentAccount.branchName,
          accountNumber: account.accountNumber,
          iban: account.iban,
          currency: account.currency,
          balance: account.balance,
          createdAt: account.createdAt,
          updatedAt: account.updatedAt,
          isActive: account.isActive,
          subAccounts: parentAccount.subAccounts,
          isSubAccountOperation: true,
          subAccountId: account.id,
          subAccountName: account.accountName
        });
      }
    } else {
      // Ana hesap için kaynak hesap bilgisi
      setBankToCashSourceAccount({
        ...account,
        isSubAccountOperation: false
      });
    }
    setIsBankToCashTransferModalOpen(true);
  };
  
  const handleCloseBankToCashTransferModal = () => {
    setIsBankToCashTransferModalOpen(false);
    setBankToCashSourceAccount(null);
    // Hesapları yeniden yükle
    if (companyId) {
      loadBankAccounts(companyId);
    }
  };

  // Excel'e aktarma fonksiyonu
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('bank.excel.accounts.sheetName'));
      
      // Sütunları tanımla
      worksheet.columns = [
        { header: t('bank.excel.accounts.columns.bankName'), key: 'bankName', width: 25 },
        { header: t('bank.excel.accounts.columns.branch'), key: 'branchName', width: 25 },
        { header: t('bank.excel.accounts.columns.accountNumber'), key: 'accountNumber', width: 20 },
        { header: t('bank.excel.accounts.columns.accountType'), key: 'accountType', width: 20 },
        { header: 'IBAN', key: 'iban', width: 30 },
        { header: t('bank.excel.accounts.columns.currency'), key: 'currency', width: 15 },
        { header: t('bank.excel.accounts.columns.balance'), key: 'balance', width: 20 },
        { header: t('bank.excel.accounts.columns.status'), key: 'isActive', width: 10 },
      ];
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // Ana hesapları ve alt hesaplarını birleştirerek oluşturacağımız bir veri dizisi
      const accountsToExport: (BankAccount | SubAccount)[] = [];
      
      // Tüm hesapları ve alt hesaplarını ekle
      filteredAccounts.forEach(account => {
        // Ana hesabı ekle
        accountsToExport.push(account);
        
        // Alt hesapları varsa onları da ekle
        if (account.subAccounts) {
          Object.entries(account.subAccounts).forEach(([subId, subAccount]) => {
            accountsToExport.push({
              ...subAccount,
              id: subId,
              parentId: account.id
            });
          });
        }
      });
      
      // Hesapları Excel'e aktar
      accountsToExport.forEach(account => {
        // Alt hesap mı kontrol et
        const isSubAccount = 'parentId' in account;
        
        // Hesap türünü belirle
        const accountType = getAccountType(account);
        
        // Ana hesap adını belirle
        const bankName = isSubAccount 
          ? filteredAccounts.find(a => a.id === (account as SubAccount).parentId)?.bankName || '-'
          : (account as BankAccount).bankName;
          
        worksheet.addRow({
          bankName: isSubAccount ? `${bankName} (${t('bank.labels.subAccount')}: ${(account as SubAccount).accountName})` : bankName,
          branchName: isSubAccount ? '-' : (account as BankAccount).branchName || '-',
          accountNumber: account.accountNumber,
          accountType: accountType,
          iban: account.iban || '-',
          currency: `${account.currency} (${getCurrencySymbol(account.currency)})`,
          balance: `${account.balance.toLocaleString(locale)} ${getCurrencySymbol(account.currency)}`,
          isActive: account.isActive ? t('bank.excel.accounts.status.active') : t('bank.excel.accounts.status.inactive')
        });
      });
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Dosya adına tarih ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `${t('bank.excel.accounts.filePrefix')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      setError(null); // Varsa hata mesajını temizle
      console.log(`${accountsToExport.length} adet banka hesabı Excel dosyasına aktarıldı.`);
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
      setError(t('bank.errors.exportFailed'));
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      {/* Üst Başlık ve Butonlar */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        mb: 3,
        flexDirection: { xs: 'column', md: 'row' }, 
        alignItems: { xs: 'flex-start', md: 'center' }, 
        gap: { xs: 2, md: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <AccountBalanceIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('bank.title')}
          </Typography>
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={(loading && bankAccounts.length === 0) || summaryLoading}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<AccountBalanceIcon />}
            color={bankStats.totalBalance >= 0 ? STATS_COLORS.green : STATS_COLORS.red}
            title={formatStatsCurrency(bankStats.totalBalance)}
            subtitle={t('bank.stats.totalBalance')}
          />
        </Grid>
        {!isManager && (
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<ArrowUpwardIcon />}
              color={STATS_COLORS.blue}
              title={t('bank.stats.todayFlowTitle', { in: bankSummary?.todayIncomeCount ?? 0, out: bankSummary?.todayExpenseCount ?? 0 })}
              subtitle={t('bank.stats.todayFlowSubtitle', {
                inAmount: formatStatsCurrency(bankSummary?.todayIncomeTotal ?? 0),
                outAmount: formatStatsCurrency(bankSummary?.todayExpenseTotal ?? 0),
              })}
            />
          </Grid>
        )}
        {!isManager && (
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<SavingsIcon />}
              color={(bankSummary?.pendingReceivableCount ?? 0) > 0 ? STATS_COLORS.purple : STATS_COLORS.grey}
              title={t('bank.stats.countTitle', { count: bankSummary?.pendingReceivableCount ?? 0 })}
              subtitle={t('bank.stats.pendingReceivable', { amount: formatStatsCurrency(bankSummary?.pendingReceivableTotal ?? 0) })}
            />
          </Grid>
        )}
        {!isManager && (
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<AccountBalanceWalletIcon />}
              color={(bankSummary?.pendingPaymentCount ?? 0) > 0 ? STATS_COLORS.orange : STATS_COLORS.grey}
              title={t('bank.stats.countTitle', { count: bankSummary?.pendingPaymentCount ?? 0 })}
              subtitle={t('bank.stats.pendingPayment', { amount: formatStatsCurrency(bankSummary?.pendingPaymentTotal ?? 0) })}
            />
          </Grid>
        )}
        {isManager && (
          <Grid item xs={12} sm={6} md={3}>
            <StatsCardItem
              icon={<AccountBalanceIcon />}
              color={STATS_COLORS.blue}
              title={t('bank.stats.countTitle', { count: bankStats.accountCount })}
              subtitle={t('bank.stats.accountCount', { defaultValue: 'Banka Hesabı' })}
            />
          </Grid>
        )}
      </StatsCardsContainer>

      {/* Arama/Filtre ve Butonlar - Aynı Satır */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('bank.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Desktop Butonlar - Aynı Satır */}
          <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddBankModal}
              >
                {t('bank.actions.newBankAccount')}
              </Button>
              <Button 
                variant="outlined"
                onClick={handleOpenMoreMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Mobil Butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={10}>
                <Button 
                  fullWidth
                  size="small"
                  variant="contained" 
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddBankModal}
                >
                  {t('bank.actions.newBankAccount')}
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button 
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenMoreMenu}
                  size="small"
                  sx={{ minWidth: 'auto', px: 0.5 }}
                >
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Ana İçerik */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px' 
        }}>
          <CircularProgress />
        </Box>
      ) : filteredAccounts.length === 0 ? (
        <Box sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
          borderRadius: 2
        }}>
          <AccountBalanceIcon 
            sx={{ 
              fontSize: 80, 
              color: 'primary.main',
              mb: 2,
              animation: `${pulse} 1.5s infinite ease-in-out`
            }} 
          />
          
          <Typography variant="h4" component="h1" gutterBottom color="primary.main" fontWeight="bold">
            {t('bank.empty.title')}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            {searchTerm ? t('bank.empty.search') : t('bank.empty.initial')}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Desktop: AccountingDataTable */}
          {!isMobile && (
            <AccountingDataTable<any>
              columns={bankColumns}
              rows={filteredAccounts.length > 0 ? hierarchicalAccounts : []}
              getRowId={(row) => isCreditCardItem(row) ? `credit-card-${row.id}` : isSubAccount(row) ? `sub-${row.id}` : row.id}
              getParentId={(row) => (isCreditCardItem(row) || isSubAccount(row)) ? row.parentId : null}
              loading={loading}
              defaultRowsPerPage={25}
              defaultSortField="bankName"
              defaultSortDirection="asc"
              getRowSx={getBankRowSx}
              emptyMessage={t('bank.empty.noAccounts')}
              emptyIcon={<AccountBalanceIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
              ariaLabel={t('bank.tableAriaLabel')}
              columnResizeStorageKey="bank"
            />
          )}
          
          {/* Mobile: Card View */}
          {isMobile && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {paginatedAccounts.map((account) => {
                // Kredi kartı satırlarını mobilde farklı göster
                if (isCreditCardItem(account)) {
                  const card = account;
                  return (
                    <Card 
                      key={`credit-card-mobile-${card.id}`}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'secondary.main',
                        bgcolor: 'rgba(156, 39, 176, 0.02)',
                        ml: 2
                      }}
                    >
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CreditCardIcon color="secondary" />
                          <Typography variant="subtitle1" fontWeight="bold">
                            {card.accountName}
                          </Typography>
                          <Chip label={t('bank.labels.creditCard')} size="small" color="secondary" variant="outlined" />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          **** **** **** {card.cardNumber.slice(-4)}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            {t('bank.labels.spent')}: {getCurrencySymbol(card.currency)}{card.usedAmount.toLocaleString(locale)}
                          </Typography>
                          <Typography variant="body2" color="success.main">
                            {t('bank.labels.availableLimit')}: {getCurrencySymbol(card.currency)}{Math.max(0, card.creditLimit - card.usedAmount).toLocaleString(locale)}
                          </Typography>
                        </Box>
                        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('bank.labels.limit')}: {getCurrencySymbol(card.currency)}{card.creditLimit.toLocaleString(locale)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                }

                const isSubAcc = isSubAccount(account);
                const accountType = getAccountType(account as BankAccount | SubAccount);
                
                return (
                  <Card 
                    key={isSubAcc ? `sub-${account.id}` : account.id}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: isSubAcc ? 'rgba(0, 0, 0, 0.02)' : 'background.paper'
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      {/* Başlık - Banka Adı veya Alt Hesap Adı */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          {!isSubAcc && hasExpandableItems(account as BankAccount) && (
                            <IconButton
                              size="small"
                              onClick={(e) => toggleAccountExpand(account.id.toString(), e)}
                              sx={{ p: 0.5 }}
                            >
                              {expandedAccounts[account.id] ? 
                                <KeyboardArrowDownIcon fontSize="small" /> : 
                                <KeyboardArrowRightIcon fontSize="small" />
                              }
                            </IconButton>
                          )}
                          <AccountBalanceIcon color="primary" sx={{ fontSize: 20 }} />
                          <Typography variant="subtitle1" fontWeight="bold">
                            {isSubAcc ? (account as SubAccount).accountName : (account as BankAccount).bankName}
                          </Typography>
                        </Box>
                        <Chip 
                          label={accountType}
                          size="small"
                          color="default"
                        />
                      </Box>
                      
                      {/* Alt Hesap işareti */}
                      {isSubAcc && (
                        <Box sx={{ mb: 1, pl: 2 }}>
                          <Chip 
                            label={t('bank.labels.subAccount')}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </Box>
                      )}
                      
                      {/* Şube (sadece ana hesaplarda) */}
                      {!isSubAcc && (
                        <>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('bank.labels.bankBranch')}:
                            </Typography>
                            <Typography variant="body2">
                              {(account as BankAccount).branchName || '-'}
                            </Typography>
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('bank.labels.companyBranch')}:
                            </Typography>
                            <Typography variant="body2">
                              {getBranchName((account as BankAccount).branchId)}
                            </Typography>
                          </Box>
                        </>
                      )}
                      
                      {/* Hesap Numarası */}
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('bank.labels.accountNo')}:
                        </Typography>
                        <Typography variant="body2">
                          {(account as BankAccount | SubAccount).accountNumber}
                        </Typography>
                      </Box>
                      
                      {/* IBAN */}
                      {(account as BankAccount | SubAccount).iban && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            IBAN:
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                            {(account as BankAccount | SubAccount).iban}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Para Birimi ve Bakiye */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('bank.labels.currency')}:
                          </Typography>
                          <Typography variant="body2">
                            {(account as BankAccount | SubAccount).currency} ({getCurrencySymbol((account as BankAccount | SubAccount).currency)})
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('bank.labels.balance')}:
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color={getTotalBalance(account as BankAccount | SubAccount) >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {getTotalBalance(account as BankAccount | SubAccount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol((account as BankAccount | SubAccount).currency)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ justifyContent: isManager ? 'flex-end' : 'space-between', px: 2, py: 1.5 }}>
                      {!isManager && (
                        <Button
                          size="small"
                          variant="contained"
                          color="info"
                          startIcon={<InfoIcon fontSize="small" />}
                          onClick={() => handleOpenAccountDetailsModal(account as BankAccount | SubAccount)}
                        >
                          {t('bank.actions.process')}
                        </Button>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenRowMenu(account as BankAccount | SubAccount, e)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        
                        {/* Satır Menüsü */}
                        <Menu
                          anchorEl={anchorElRow[getMenuKey(account as BankAccount | SubAccount)]}
                          open={Boolean(anchorElRow[getMenuKey(account as BankAccount | SubAccount)])}
                          onClose={() => handleCloseRowMenu(account as BankAccount | SubAccount)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              handleCloseRowMenu(account as BankAccount | SubAccount);
                              if (isSubAcc) {
                                // Alt hesap düzenleme
                                const parentAcc = filteredAccounts.find(a => a.id === (account as SubAccount).parentId);
                                if (parentAcc) {
                                  handleEditSubAccount(account as SubAccount, parentAcc);
                                }
                              } else {
                                // Ana hesap düzenleme
                                handleEditAccount(account as BankAccount);
                              }
                            }}
                          >
                            <ListItemIcon>
                              <EditIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={t('bank.actions.edit')} />
                          </MenuItem>
                          {!isManager && (
                            <MenuItem
                              onClick={() => {
                                handleCloseRowMenu(account as BankAccount | SubAccount);
                                handleOpenBankToBankTransferModal(account as BankAccount | SubAccount);
                              }}
                            >
                              <ListItemIcon>
                                <SwapHorizIcon fontSize="small" color="info" />
                              </ListItemIcon>
                              <ListItemText primary={t('bank.actions.bankTransfer')} />
                            </MenuItem>
                          )}
                          {!isManager && (
                            <MenuItem
                              onClick={() => {
                                handleCloseRowMenu(account as BankAccount | SubAccount);
                                handleOpenBankToCashTransferModal(account as BankAccount | SubAccount);
                              }}
                            >
                              <ListItemIcon>
                                <AccountBalanceWalletIcon fontSize="small" color="warning" />
                              </ListItemIcon>
                              <ListItemText primary={t('bank.actions.cashTransfer')} />
                            </MenuItem>
                          )}
                          {!isManager && !isSubAcc && (
                            <MenuItem
                              onClick={() => {
                                handleCloseRowMenu(account as BankAccount | SubAccount);
                                setSelectedAccountForDetails(account as BankAccount);
                                setIsAddSubAccountModalOpen(true);
                              }}
                            >
                              <ListItemIcon>
                                <AddIcon fontSize="small" color="success" />
                              </ListItemIcon>
                              <ListItemText primary={t('bank.actions.addSubAccount')} />
                            </MenuItem>
                          )}
                          {!isManager && !isSubAcc && (
                            <MenuItem
                              onClick={() => {
                                handleCloseRowMenu(account as BankAccount | SubAccount);
                                handleOpenAddCreditCardModal(account as BankAccount);
                              }}
                            >
                              <ListItemIcon>
                                <CreditCardIcon fontSize="small" color="secondary" />
                              </ListItemIcon>
                              <ListItemText primary={t('bank.actions.addCreditCard')} />
                            </MenuItem>
                          )}
                          {!isManager && (
                            <MenuItem
                              onClick={() => {
                                handleCloseRowMenu(account as BankAccount | SubAccount);
                                handleOpenDeleteDialog(isSubAcc ? 
                                  {
                                    ...(account as SubAccount),
                                    bankName: filteredAccounts.find(a => a.id === (account as SubAccount).parentId)?.bankName || '',
                                    branchName: filteredAccounts.find(a => a.id === (account as SubAccount).parentId)?.branchName || ''
                                  } as BankAccount : 
                                  account as BankAccount
                                );
                              }}
                            >
                              <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                              </ListItemIcon>
                              <ListItemText primary={t('bank.actions.delete')} />
                            </MenuItem>
                          )}
                        </Menu>
                      </Box>
                    </CardActions>
                  </Card>
                );
              })}
              
              {/* Mobil Sayfalama */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                mt: 2,
                pb: 2
              }}>
                <Typography variant="caption" color="text.secondary">
                  {t('bank.pagination.showing', { count: hierarchicalAccounts.length, from: page * rowsPerPage + 1, to: Math.min((page + 1) * rowsPerPage, hierarchicalAccounts.length) })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={page === 0}
                    onClick={(e) => handleChangePage(e, page - 1)}
                  >
                    {t('bank.pagination.previous')}
                  </Button>
                  <Typography variant="body2">
                    {t('bank.pagination.page', { page: page + 1, total: Math.ceil(hierarchicalAccounts.length / rowsPerPage) })}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={page >= Math.ceil(hierarchicalAccounts.length / rowsPerPage) - 1}
                    onClick={(e) => handleChangePage(e, page + 1)}
                  >
                    {t('bank.pagination.next')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Modallar */}
      <AddBankAccount 
        open={isAddBankModalOpen} 
        onClose={handleCloseAddBankModal}
        accountToEdit={accountToEdit}
        lockedBranchId={isManager ? managerBranchId : null}
      />
      
      {/* Alt Hesap Ekleme Modalı */}
      {selectedAccountForDetails && (
        <AddSubAccount
          open={isAddSubAccountModalOpen}
          onClose={handleCloseAddSubAccountModal}
          parentAccount={selectedAccountForDetails}
          companyId={companyId}
          subAccountToEdit={subAccountToEdit}
        />
      )}

      {/* Silme Onay Diyaloğu */}
      <Dialog
        open={deleteDialogOpen}
        onClose={!isDeleting ? handleCloseDeleteDialog : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {canDelete ? t('bank.deleteDialog.title') : t('bank.deleteDialog.notAllowedTitle')}
        </DialogTitle>
        <DialogContent>
          {deleteCheckLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
              <CircularProgress size={32} />
              <Typography sx={{ ml: 2 }}>{t('bank.deleteDialog.checking')}</Typography>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {!canDelete ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('bank.deleteDialog.notAllowedWarning')}
                </Alert>
              ) : null}
              <DialogContentText>
                {selectedAccount ? (
                  <>
                    <strong>{selectedAccount.bankName}</strong> bankasına ait <strong>{selectedAccount.accountNumber}</strong> 
                    {' '}{t('bank.deleteDialog.numberedAccount')}
                    {canDelete ? (
                      <span> {t('bank.deleteDialog.confirm')} <strong>{t('bank.deleteDialog.irreversible')}</strong></span>
                    ) : (
                      <span> {t('bank.deleteDialog.hasHistory')}</span>
                    )}
                  </>
                ) : (
                  canDelete ? 
                    t('bank.deleteDialog.defaultConfirm') :
                    t('bank.deleteDialog.defaultNotAllowed')
                )}
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseDeleteDialog} 
            color="primary"
            disabled={isDeleting}
          >
            {canDelete ? t('bank.common.cancel') : t('bank.common.ok')}
          </Button>
          {canDelete && (
            <Button 
              onClick={handleDeleteAccount} 
              color="error" 
              disabled={isDeleting || deleteCheckLoading}
              startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {isDeleting ? t('bank.common.deleting') : t('bank.actions.delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Hesap Detayları Modalı */}
      <Dialog
        open={isAccountDetailsModalOpen}
        onClose={handleCloseAccountDetailsModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: { xs: 1, sm: 2 } // Mobilde kenar boşluklarını azalttık
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', pr: 6 }}>
          {t('bank.details.title')}
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseAccountDetailsModal}
            aria-label={t('bank.common.close')}
            sx={{ position: 'absolute', right: 16, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, overflowX: 'hidden' }}>
          {selectedAccountForDetails && (
            <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                {selectedAccountForDetails.bankName}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">{t('bank.labels.bankBranch')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedAccountForDetails.branchName || '-'}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">{t('bank.labels.companyBranch')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {isSubAccount(selectedAccountForDetails) 
                        ? '-' 
                        : getBranchName((selectedAccountForDetails as BankAccount).branchId)}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">{t('bank.labels.accountNo')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedAccountForDetails.accountNumber}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">IBAN:</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', wordBreak: 'break-all', fontWeight: 500 }}>
                      {selectedAccountForDetails.iban || '-'}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">{t('bank.labels.currency')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedAccountForDetails.currency} ({getCurrencySymbol(selectedAccountForDetails.currency)})</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">{t('bank.labels.accountType')}:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{getAccountType(selectedAccountForDetails)}</Typography>
                  </Box>
                </Box>
                
                {!isSubAccount(selectedAccountForDetails) && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('bank.labels.authorizedPerson')}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedAccountForDetails as BankAccount).authorizedPerson || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">{t('bank.labels.authorizedPhone')}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(selectedAccountForDetails as BankAccount).authorizedPhone || '-'}
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">{t('bank.labels.currentBalance')}:</Typography>
                  <Typography 
                    variant="h5" 
                    color={selectedAccountForDetails.balance >= 0 ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                  >
                    {selectedAccountForDetails.balance.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(selectedAccountForDetails.currency)}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {t('bank.details.accountActions')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ArrowUpwardIcon />}
                  size="large"
                  sx={{ py: 1.5, flex: 1 }}
                  onClick={handleOpenDepositModal}
                >
                  {t('bank.actions.deposit')}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ArrowDownwardIcon />}
                  size="large"
                  sx={{ py: 1.5, flex: 1 }}
                  onClick={handleOpenWithdrawModal}
                >
                  {t('bank.actions.withdraw')}
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<SwapHorizIcon />}
                  size="large"
                  sx={{ py: 1.5, flex: 1 }}
                  onClick={() => {
                    handleCloseAccountDetailsModal();
                    handleOpenBankToBankTransferModal(selectedAccountForDetails);
                  }}
                >
                  {t('bank.actions.bankTransfer')}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<AccountBalanceWalletIcon />}
                  size="large"
                  sx={{ py: 1.5, flex: 1 }}
                  onClick={() => {
                    handleCloseAccountDetailsModal();
                    handleOpenBankToCashTransferModal(selectedAccountForDetails);
                  }}
                >
                  {t('bank.actions.cashTransfer')}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Para Yatırma Modalı */}
      {selectedAccountForDeposit && (
        <BankDepositModal
          open={isDepositModalOpen}
          onClose={handleCloseDepositModal}
          bankAccountId={selectedAccountForDeposit.id}
          bankAccountName={selectedAccountForDeposit.accountNumber}
          bankName={selectedAccountForDeposit.bankName}
          currency={selectedAccountForDeposit.currency}
          companyId={companyId || ''}
          currencySymbol={getCurrencySymbol(selectedAccountForDeposit.currency)}
          balance={selectedAccountForDeposit.balance}
          // Alt hesap işlemi yapılıyorsa gerekli bilgileri aktar
          subAccountInfo={selectedAccountForDeposit.isSubAccountOperation ? {
            subAccountId: selectedAccountForDeposit.subAccountId || '',
            subAccountName: selectedAccountForDeposit.subAccountName || ''
          } : undefined}
          onSuccess={() => {
            // İşlem başarılı olduğunda yapılacaklar
            setError(null);
          }}
        />
      )}

      {/* Para Çekme Modalı */}
      {selectedAccountForWithdraw && (
        <BankWithdrawModal
          open={isWithdrawModalOpen}
          onClose={handleCloseWithdrawModal}
          bankAccountId={selectedAccountForWithdraw.id}
          bankAccountName={selectedAccountForWithdraw.accountNumber}
          bankName={selectedAccountForWithdraw.bankName}
          currency={selectedAccountForWithdraw.currency}
          companyId={companyId || ''}
          currencySymbol={getCurrencySymbol(selectedAccountForWithdraw.currency)}
          balance={selectedAccountForWithdraw.balance}
          // Alt hesap işlemi yapılıyorsa gerekli bilgileri aktar
          subAccountInfo={selectedAccountForWithdraw.isSubAccountOperation ? {
            subAccountId: selectedAccountForWithdraw.subAccountId || '',
            subAccountName: selectedAccountForWithdraw.subAccountName || ''
          } : undefined}
          onSuccess={() => {
            // İşlem başarılı olduğunda yapılacaklar
            setError(null);
          }}
        />
      )}

      {/* Kasa Transfer Modalı */}
      {companyId && (
        <CashTransferModal
          open={isCashTransferModalOpen}
          onClose={handleCloseCashTransferModal}
          companyId={companyId}
          onSuccess={() => {
            // İşlem başarılı olduğunda yapılacaklar
            setError(null);
          }}
        />
      )}

      {/* Hesaba Virman Modalı */}
      {companyId && (
        <AccountTransferModal
          open={isAccountTransferModalOpen}
          onClose={handleCloseTransferModal}
          companyId={companyId}
          onSuccess={() => {
            // İşlem başarılı olduğunda yapılacaklar
            setError(null);
          }}
        />
      )}

      {/* Bankadan Bankaya Transfer Modalı */}
      {companyId && bankTransferSourceAccount && (
        <BankToBankTransferModal
          open={isBankToBankTransferModalOpen}
          onClose={handleCloseBankToBankTransferModal}
          sourceBankAccount={{
            id: bankTransferSourceAccount.id,
            bankName: bankTransferSourceAccount.bankName,
            accountNumber: bankTransferSourceAccount.accountNumber,
            balance: bankTransferSourceAccount.balance,
            currency: bankTransferSourceAccount.currency,
            isSubAccount: bankTransferSourceAccount.isSubAccountOperation,
            subAccountId: bankTransferSourceAccount.subAccountId,
            subAccountName: bankTransferSourceAccount.subAccountName
          }}
          companyId={companyId}
          onSuccess={() => {
            setError(null);
          }}
        />
      )}

      {/* Bankadan Kasaya Transfer Modalı */}
      {companyId && bankToCashSourceAccount && (
        <BankToCashTransferModal
          open={isBankToCashTransferModalOpen}
          onClose={handleCloseBankToCashTransferModal}
          sourceBankAccount={{
            id: bankToCashSourceAccount.id,
            bankName: bankToCashSourceAccount.bankName,
            accountNumber: bankToCashSourceAccount.accountNumber,
            balance: bankToCashSourceAccount.balance,
            currency: bankToCashSourceAccount.currency,
            isSubAccount: bankToCashSourceAccount.isSubAccountOperation,
            subAccountId: bankToCashSourceAccount.subAccountId,
            subAccountName: bankToCashSourceAccount.subAccountName
          }}
          companyId={companyId}
          onSuccess={() => {
            setError(null);
          }}
        />
      )}

      {/* 3 Nokta Menüsü */}
      <Menu
        anchorEl={anchorElMore}
        open={Boolean(anchorElMore)}
        onClose={handleCloseMoreMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          handleCloseMoreMenu();
          if (companyId) {
            console.log('🔄 [BANK] Manuel yenileme yapılıyor...');
            loadBankAccounts(companyId);
          }
        }}>
          <ListItemIcon>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('bank.actions.refresh')} />
        </MenuItem>
        
        {!isManager && (
          <MenuItem onClick={() => {
            handleCloseMoreMenu();
            navigate('/kredi-kartlari');
          }}>
            <ListItemIcon>
              <CreditCardIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText primary={t('bank.actions.creditCards')} />
          </MenuItem>
        )}

        {!isManager && (
          <MenuItem onClick={() => {
            handleCloseMoreMenu();
            navigate('/krediler');
          }}>
            <ListItemIcon>
              <RequestQuoteIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary={locale === 'tr-TR' ? 'Krediler' : 'Loans'} />
          </MenuItem>
        )}

        {!isManager && (
          <MenuItem onClick={() => {
            handleCloseMoreMenu();
            handleOpenTransactionReportModal();
          }}>
            <ListItemIcon>
              <AssessmentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('bank.actions.movementReport')} />
          </MenuItem>
        )}
        
        <MenuItem 
          onClick={() => {
            handleCloseMoreMenu();
            exportToExcel();
          }}
          disabled={loadingExport || loading || filteredAccounts.length === 0}
        >
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={loadingExport ? t('bank.actions.preparing') : t('bank.actions.downloadExcel')} />
        </MenuItem>
      </Menu>

      {/* Banka Kredisi Ekleme Modalı */}
      <AddLoanModal
        open={addLoanModalOpen}
        onClose={() => { setAddLoanModalOpen(false); setLoanBankAccount(null); }}
        onSuccess={() => {
          setLoanSnackbar(locale === 'tr-TR' ? 'Kredi eklendi' : 'Loan added');
          if (companyId) loadBankAccounts(companyId);
        }}
        preselectedBankAccountId={loanBankAccount ? parseInt(loanBankAccount.id, 10) : undefined}
      />
      <Snackbar
        open={Boolean(loanSnackbar)}
        autoHideDuration={4000}
        onClose={() => setLoanSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setLoanSnackbar(null)}>{loanSnackbar}</Alert>
      </Snackbar>

      {/* Kredi Kartı Ekleme Modalı */}
      <AddCreditCardModal
        open={isAddCreditCardModalOpen}
        onClose={handleCloseAddCreditCardModal}
        bankAccountId={selectedBankForCreditCard ? parseInt(selectedBankForCreditCard.id) : undefined}
        bankAccountName={selectedBankForCreditCard?.bankName}
        cardToEdit={creditCardToEdit as any}
      />

      <CreditCardUsageModal
        open={isCreditCardUsageModalOpen}
        onClose={() => setIsCreditCardUsageModalOpen(false)}
        card={creditCardActionTarget}
        onSuccess={handleCreditCardActionSuccess}
      />

      <CreditCardPaymentModal
        open={isCreditCardPaymentModalOpen}
        onClose={() => setIsCreditCardPaymentModalOpen(false)}
        card={creditCardActionTarget}
        onSuccess={handleCreditCardActionSuccess}
      />

      <CreditCardTransactionsModal
        open={isCreditCardTxModalOpen}
        onClose={() => setIsCreditCardTxModalOpen(false)}
        card={creditCardActionTarget}
        onChanged={() => {
          // Hareket silindiğinde kart borcu ve (PAYMENT iadesi olduysa) banka bakiyeleri değişir
          if (companyId) {
            loadCreditCards(companyId);
            loadBankAccounts(companyId);
          }
        }}
      />
    </>
  );
};

export default Bank; 